import { memo, type ReactNode } from "react";

import { ApolloProvider } from "@apollo/client";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import { apolloClient } from "@/lib/apollo/client";
import { persistor, store } from "@/store";
import ToastProvider from "@/components/ui/ToastProvider";

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Single wrapper that composes all context providers.
 * Order matters:
 *   1. ReduxProvider  — store must be available to everything below
 *   2. PersistGate    — holds render until rehydration is complete
 *   3. ApolloProvider — client available to all hooks/queries below
 *
 * Add new providers here (e.g. ThemeProvider, ToastProvider) as the
 * app grows. Keep this file flat — no logic, only composition.
 */
const Providers = memo(function Providers({ children }: ProvidersProps) {
  return (
    <ReduxProvider store={store}>
      <PersistGate loading={<AppLoadingScreen />} persistor={persistor}>
        <ApolloProvider client={apolloClient}>
          <ToastProvider>{children}</ToastProvider>
        </ApolloProvider>
      </PersistGate>
    </ReduxProvider>
  );
});

// ─── Minimal loading screen shown during redux-persist rehydration ─
function AppLoadingScreen() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-neutral-950">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-neutral-700 border-t-primary-500" />
        <span className="text-sm text-neutral-400">Loading…</span>
      </div>
    </div>
  );
}

export default Providers;
